import {
    AnnuleeConseiller,
    AnnuleeSysteme,
    Confirmation,
    Deeplink,
    Erreur,
    ErreurFonctionnelle,
    EveilAssurance,
    Home,
    InformationsSignature,
    InteretRetard,
    Legal,
    Modalites,
    ModalitesDatePaiement,
    Offres,
    Signature,
    Validation
} from './views';
import {
    Box,
    Theme,
    ThemeProvider,
    createStyles,
    makeStyles,
    useTheme
} from '@material-ui/core';
import {
    CodeAvis,
    DebugBanner,
    ErrorBanner,
    Header,
    NoteLegaleEveilAssurance,
    NoteLegaleOffre,
    SupportBar,
    injecterClavardage,
    setJetonClavardage
} from './components';
import {
    ContexteAppel,
    DOMAIN_DESJARDINS_COOKIE,
    JWT_COOKIE_CONFIG_NAME,
    Logger,
    MOBILE_WIDTH,
    QUERY_PARAM_DEEP_LINK,
    ScrollToTop,
    TNR_COOKIE,
    URLToURLSearchParams,
    deleteCookie,
    addDynatraceTagScript,
    getLangue,
    getPretIdDemande,
    isMobileAppFromCookies
} from './utils';
import { EnvConfig, EnvType, getEnvConfig, isEnvNonProd } from './config';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Footer, GridHelper, LoadingScreen } from './styles';
import {
    PersonneConnectee,
    Routes,
    getActualRoute,
    isRouteRequireLeaveConfirmation,
    useFakeTokenApi,
    useMock,
    useRenouvellementApi,
    useRoutes,
    useSessionApi,
    useToggleApi
} from './hooks';
import { Provider, useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import {
    Route,
    HashRouter as Router,
    Switch,
    useHistory
} from 'react-router-dom';
import {
    hideLoading,
    initCybermetrie,
    initGAServices,
    setFlowData,
    setIdDemande,
    setRenouvellementData,
    setSessionData
} from './features';
import { isMockEnabled, queryClient, shouldUseMock } from './api';

import { AxiosError } from 'axios';
import { CODE_ERREUR_FONCTIONNELLE_RHN } from './views/erreurFonctionnelle/ErreurFonctionnelle.connected';
import { CODE_ERREUR_TECHNIQUE_RHN } from './views/erreur/Erreur.connected';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import i18n from 'i18next';
import { isRouteRequireStatutCheck } from './hooks/navigation/useRoutes/useRoutes';
import moment from 'moment';
import store from './store';
import theme from './theme/theme.jsx';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import * as authentification from '@oel/js-authentification';
import {
    deleteJWT,
    getCookieAttributes,
    setJWT,
    setLangueInCookies
} from './utils/cookieParser';

const removeFocusForButton = () => {
    document.addEventListener('click', function () {
        if (
            document.activeElement instanceof HTMLElement &&
            document.activeElement.toString() == '[object HTMLButtonElement]'
        ) {
            document.activeElement.blur();
        }
    });
};

const getContainerStyles = (
    theme: Theme,
    mobileDownMarginTop = '0',
    mobileUpMarginTop = '0',
    mobileDownMarginBottom = 'auto',
    mobileUpMarginBottom = 'auto'
) => ({
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
        margin: `${mobileDownMarginTop} 0 ${mobileDownMarginBottom} 0`
    },
    [theme.breakpoints.up(MOBILE_WIDTH)]: {
        margin: `${mobileUpMarginTop} 0 ${mobileUpMarginBottom} 0`
    }
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        autoContainer: getContainerStyles(theme),
        viewWithLegalContainer: getContainerStyles(
            theme,
            '1rem',
            '2rem',
            '0',
            '0'
        ),
        viewContainer: getContainerStyles(theme, '1rem', '2rem', '1rem', '1rem')
    })
);

const RenderFooter = () => {
    // Oblige d'utilise une variable plutot que le i18n direct
    // Sinon la lib oel ne prend pas en compte
    const langue = i18n.language;
    return <>{langue && <Footer langue={langue}></Footer>}</>;
};

const RenderHeader = () => {
    return (
        <>
            {i18n.language && (
                <Header
                    langue={i18n.language}
                    isConfirmationRequired={isRouteRequireLeaveConfirmation()}
                    isStatutCheckRequired={isRouteRequireStatutCheck()}
                />
            )}
        </>
    );
};

const RenderErreur = (info: FallbackProps) => {
    let axiosError: AxiosError<any>;
    let isTechnicalError = false;
    let codeErreur: number;
    let codeErreurRHN: string;
    let messageErreur: string;
    let versionTexteErreurPaiementEnAttente: string;
    let revenirJourSemaineErreurPaiementEnAttente: string;
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    const langue = getLangue();
    // Recuperation depuis le store pour savoir si les appels ont reussis
    const idDemande = useSelector(
        (state: any) => state.renouvellement.idDemande
    );

    // Try to retrieve error and cast it to Axios type
    try {
        if (info?.error) {
            axiosError = info?.error as AxiosError<any>;
            codeErreur = axiosError?.response?.status;

            // retrieve error & try to find error type
            const errors = axiosError?.response?.data?.errors;
            codeErreurRHN =
                errors && errors.length > 0 && errors[0]?.code
                    ? errors[0]?.code
                    : null;
            messageErreur =
                errors && errors.length > 0 && errors[0]?.message
                    ? errors[0]?.message
                    : null;
            versionTexteErreurPaiementEnAttente =
                codeErreurRHN ===
                    CODE_ERREUR_FONCTIONNELLE_RHN.ERREUR_PAIEMENT_EN_ATTENTE &&
                errors &&
                errors.length > 0 &&
                errors[0]?.meta &&
                errors[0]?.meta?.versionTexte
                    ? errors[0]?.meta?.versionTexte
                    : null;
            revenirJourSemaineErreurPaiementEnAttente =
                codeErreurRHN ===
                    CODE_ERREUR_FONCTIONNELLE_RHN.ERREUR_PAIEMENT_EN_ATTENTE &&
                errors &&
                errors.length > 0 &&
                errors[0]?.meta &&
                errors[0]?.meta?.revenirJourSemaine
                    ? errors[0]?.meta?.revenirJourSemaine
                    : null;
            isTechnicalError =
                codeErreurRHN === CODE_ERREUR_TECHNIQUE_RHN.ERREUR_TECHNIQUE;
        }
    } catch (error) {
        axiosError = null;
    }

    history.push(`./${Routes.ERREUR}`);

    useEffect(() => {
        i18n.changeLanguage(langue);
        moment.locale(langue);
    }, []);

    return (
        <Provider store={store}>
            <Box
                display="flex"
                flexDirection="column"
                bgcolor={theme.palette.grey[200]}
                minHeight="100vh"
            >
                {idDemande && <RenderHeader />}
                {/* If error is one of axios -> display error banner */}
                {axiosError && <ErrorBanner axiosError={axiosError} />}
                <div className={classes.autoContainer}>
                    <div className={classes.viewContainer}>
                        {/* Si on a pu charger la session alors erreur fonctionnelle. Sinon technique */}
                        {isTechnicalError ? (
                            <Erreur
                                codeErreur={codeErreur}
                                axiosError={axiosError}
                            />
                        ) : (
                            <ErreurFonctionnelle
                                status={codeErreur}
                                codeErreurRHN={codeErreurRHN}
                                messageErreur={messageErreur}
                                axiosError={axiosError}
                                versionTexteErreurPaiementEnAttente={
                                    versionTexteErreurPaiementEnAttente
                                }
                                revenirJourSemaineErreurPaiementEnAttente={
                                    revenirJourSemaineErreurPaiementEnAttente
                                }
                            />
                        )}
                    </div>
                </div>
                {/* Afficher le bandeau de support pour page fonctionnelle sauf si l'erreur est dans le calcul offre */}
                {idDemande &&
                    codeErreurRHN !==
                        CODE_ERREUR_FONCTIONNELLE_RHN.ERREUR_CALCUL_OFFRE &&
                    codeErreurRHN !==
                        CODE_ERREUR_FONCTIONNELLE_RHN.ERREUR_PAIEMENT_EN_ATTENTE && (
                        <SupportBar
                            contexte={ContexteAppel.PAGE_ERREUR}
                            idDemande={idDemande}
                        />
                    )}
                <RenderFooter />
            </Box>
        </Provider>
    );
};

const RenderViewSelected = (route: string, isConseiller: boolean) => {
    //Render view
    switch (route) {
        case Routes.HOME:
            return <Home />;
        case Routes.OFFRES:
            return <Offres />;
        case Routes.MODALITES:
            return <Modalites />;
        case Routes.MODALITES_DATE_PAIEMENT:
            return <ModalitesDatePaiement />;
        case Routes.INFORMATIONS_SIGNATURE:
            return <InformationsSignature />;
        case Routes.VALIDATION:
            return <Validation />;
        case Routes.CONFIRMATION:
            return <Confirmation />;
        case Routes.ERREUR:
            return <ErreurFonctionnelle isConseiller={isConseiller} />;
        case Routes.ANNULEE_SYSTEME:
            return <AnnuleeSysteme />;
        case Routes.ANNULEE_CONSEILLER:
            return <AnnuleeConseiller />;
        case Routes.LEGAL:
            return <Legal />;
        case Routes.EVEIL_ASSURANCE:
            return <EveilAssurance />;
        case Routes.INTERET_RETARD:
            return <InteretRetard />;
        case Routes.SIGNATURE:
            return <Signature />;
        default:
            return <></>;
    }
};

const RenderComponent = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { getInitialRoute, config, getRoute } = useRoutes();

    // Apis
    const { data: session } = useSessionApi();
    const { data: renouvellement } = useRenouvellementApi(
        !!session && !session?.isFastLoading
    );

    const [isRouteLoaded, setIsRouteLoaded] = useState(false);

    const idDemandeStored = useSelector(
        (state: any) => state.renouvellement.idDemande
    );

    useEffect(() => {
        if (isRouteLoaded == true) {
            dispatch(setIdDemande(session?.contextePret?.idDemande));
        }
    }, [isRouteLoaded]);

    // Check if idDemandeStored exists. IF not -> reloaded page protection -> nav to PageInitiale
    useEffect(() => {
        if (session && config && (session?.isFastLoading || renouvellement)) {
            if (idDemandeStored === null) {
                // Si on est en fast loading -> On prend la page initiale de session
                // Sinon on attend les regles d'affaire et on prend la page initiale de renouvellement
                const route = getInitialRoute(
                    session?.isFastLoading
                        ? session?.pageInitiale
                        : renouvellement?.pageInitiale,
                    renouvellement &&
                        renouvellement?.montantTotalInteretRetard > 0,
                    renouvellement?.pretAdmissibleAssurance === true
                );

                history.push(`./${route}`);
                setIsRouteLoaded(true);
            }
        }
    }, [session, config, renouvellement]);

    //Save route to store if defined
    if (idDemandeStored && getRoute()?.route) {
        dispatch(setFlowData(getRoute()?.route));
    }

    // Render view after idDemandeStored is defined
    return RenderViewSelected(
        idDemandeStored ? getRoute()?.route : null,
        session?.isConseiller
    );
};

const RenderSupportBar = () => {
    const { getRoute } = useRoutes();

    const { data: session } = useSessionApi();
    const idDemande = session?.contextePret?.idDemande;

    let contexte = null;

    if (session?.personneConnectee !== PersonneConnectee.MEMBRE) {
        return null;
    }

    switch (getRoute()?.route) {
        case Routes.HOME:
            contexte = ContexteAppel.PAGE_ACCUEIL;
            break;
        case Routes.OFFRES:
            contexte = ContexteAppel.PAGE_OFFRE;
            break;
        case Routes.MODALITES:
            contexte = ContexteAppel.PAGE_MODALITES;
            break;
        case Routes.MODALITES_DATE_PAIEMENT:
            contexte = ContexteAppel.PAGE_DATE_PAIEMENT;
            break;
        case Routes.INFORMATIONS_SIGNATURE:
            contexte = ContexteAppel.PAGE_INFO_SIGNATURE;
            break;
        case Routes.VALIDATION:
            contexte = ContexteAppel.PAGE_VALIDATION;
            break;
        case Routes.CONFIRMATION:
            contexte = ContexteAppel.PAGE_CONFIRMATION;
            break;
        case Routes.SIGNATURE:
            contexte = ContexteAppel.PAGE_SIGNATURE;
            break;
        case Routes.EVEIL_ASSURANCE:
            contexte = ContexteAppel.PAGE_EVEIL_ASSURANCE;
            break;
        case Routes.ANNULEE_SYSTEME:
            contexte = ContexteAppel.PAGE_ERREUR;
            break;
        case Routes.ANNULEE_CONSEILLER:
            contexte = ContexteAppel.PAGE_ERREUR;
            break;
        case Routes.ERREUR:
            contexte = ContexteAppel.PAGE_ERREUR;
            break;
    }

    return contexte && <SupportBar contexte={contexte} idDemande={idDemande} />;
};

const RenderNoteLegal = () => {
    const { getRoute } = useRoutes();

    switch (getRoute()?.route) {
        case Routes.OFFRES:
            return <NoteLegaleOffre />;
        case Routes.EVEIL_ASSURANCE:
            return <NoteLegaleEveilAssurance />;
        default:
            return <></>;
    }
};

const RenderRoutes: React.FC<{
    isConseiller: boolean;
    isDeepLink: boolean;
}> = ({ isConseiller, isDeepLink }) => {
    // Vue conseiller lecture seule
    if (isConseiller) {
        return <ErreurFonctionnelle isConseiller={true} />;
    }
    // Vue deep link
    if (isDeepLink) {
        return <Deeplink />;
    }
    // Routage classique une fois dans le parcours
    return (
        <Switch>
            <Route path={'/:path'}>
                <RenderComponent />
            </Route>
            <Route path={'/'}>
                <RenderComponent />
            </Route>
            <Route path="*">
                <div>no match</div>
            </Route>
        </Switch>
    );
};

// Dynamic Content: Views etc
const View: React.FC<{
    envConfig: EnvConfig;
    langue: string;
    isAuth0Enabled: boolean;
    isAuthenticated: boolean;
    isNonProdEnv: boolean;
    isDeepLink: boolean;
    isMobileApp: boolean;
}> = ({
    envConfig,
    langue,
    isAuth0Enabled,
    isAuthenticated,
    isNonProdEnv,
    isDeepLink,
    isMobileApp
}) => {
    // APIs
    const { data: session } = useSessionApi(isDeepLink === false);
    const { data: renouvellement } = useRenouvellementApi(
        session?.isConseiller === false
    );

    // Prerequisite data loading (session & renouvellement)
    const [isPrerequisiteDataLoading, setPrerequisiteDataLoading] =
        useState(true);

    // Store
    const isCybermetrieEnabled = useSelector(
        (state: any) => state.cybermetrie.isEnabled
    );

    removeFocusForButton();

    // hooks
    const [t] = useTranslation(['General']);
    const dispatch = useDispatch();
    const classes = useStyles();

    // Const
    const platform = isMobileApp ? 'Mobile' : 'Web';
    const orientation =
        window.innerHeight > window.innerWidth ? 'Portrait' : 'Landscape';
    const browser = navigator.userAgent;
    // Set title
    document.title = t('titrePagePrincipal');

    const initCyberAndFinishAppLoading = () => {
        const {
            typeParcoursEmprunteur,
            personneConnectee,
            contextePret: { idDemande }
        } = session;

        // Dispatch init cyber metrie
        dispatch(
            initCybermetrie({
                personneConnectee,
                idDemande,
                langue,
                platform,
                orientation,
                browser,
                parcoursEmprunteur: typeParcoursEmprunteur
            })
        );

        // Prerequisite data loaded
        setPrerequisiteDataLoading(false);
    };

    useEffect(() => {
        if (isPrerequisiteDataLoading && session) {
            if (session?.isFastLoading === true) {
                // Bascule rapide -> Si Guy ou Unique ET page accueil
                initCyberAndFinishAppLoading();
            } else if (renouvellement) {
                // Sinon chargement et verifs normales
                // Si on recoit un redirectUrl et qu'on est un membre -> Alors on suit l'url ( Assurance )
                if (
                    renouvellement?.redirectUrl &&
                    session?.personneConnectee === PersonneConnectee.MEMBRE
                ) {
                    window.location.href = renouvellement?.redirectUrl;
                    return;
                }
                // Sinon chargement normal
                initCyberAndFinishAppLoading();
            }
        }
        if (session && renouvellement) {
            dispatch(setRenouvellementData(renouvellement));
        }
    }, [session, renouvellement]);

    useEffect(() => {
        // Initilialisation de la cybermetrie
        if (isCybermetrieEnabled && session) {
            dispatch(initGAServices());
        }
    }, [session, isCybermetrieEnabled]);

    useEffect(() => {
        // arreter le chargement et le spinner si c'est un conseiller
        if (session?.isConseiller) {
            setPrerequisiteDataLoading(false);
            dispatch(hideLoading());
        }
        dispatch(setSessionData(session));
    }, [session]);

    useEffect(() => {
        // Charger la vue deepLink avec le spinner qui partira dans la vue une fois l'appel fini
        if (isDeepLink) {
            setPrerequisiteDataLoading(false);
        }
    }, [isDeepLink]);

    const withLegalRoutes = [Routes.OFFRES.toString()];
    const viewContainerClass = withLegalRoutes.includes(getActualRoute())
        ? classes.viewWithLegalContainer
        : classes.viewContainer;

    return (
        <>
            {/* Global loading is managed by views through redux store */}
            {!isPrerequisiteDataLoading && (
                <>
                    {isNonProdEnv && (
                        <DebugBanner
                            skipBasculeInfos={
                                isDeepLink || session?.isConseiller
                            }
                            envConfig={envConfig}
                            isAuth0Enabled={isAuth0Enabled}
                            isAuthenticated={isAuthenticated}
                            session={session}
                            renouvellement={renouvellement}
                        />
                    )}
                    {!session?.isConseiller &&
                        !isDeepLink &&
                        renouvellement &&
                        renouvellement?.montantTotalInteretRetard === 0 && (
                            <CodeAvis />
                        )}
                    <div className={viewContainerClass}>
                        <RenderRoutes
                            isConseiller={session?.isConseiller}
                            isDeepLink={isDeepLink}
                        />
                    </div>
                </>
            )}
        </>
    );
};

// Auth0 configuration
// 1. Verifier si on a besoin d'utiliser Auth0.
// 2. Initialiser auth0 avec le tokenIssuer et l'environement
const Auth0 = ({ langue, deepLinkMode, mockEnabled }) => {
    const classes = useStyles();

    // const
    const isMobileApp = isMobileAppFromCookies();
    const cookieConfigAttributes = getCookieAttributes(JWT_COOKIE_CONFIG_NAME);

    // Store
    const globalLoading = useSelector((state: any) => state.loading.show);

    // state
    const [token, setToken] = useState(null);
    const [isReadyForAPI, setIsReadyForAPI] = useState<boolean>(false);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [shouldUseAuth0, setShouldUseAuth0] = useState<boolean | null>(null);

    // hooks
    const envConfig = getEnvConfig();
    const { data: toggle } = useToggleApi(isAuthenticated === true);

    const retrieveFakeToken = (pdo: string, role?: string) => {
        Logger.log("Authentification Fake: Terminee - obtention d'un token...");
        // On recupere un token de test
        useFakeTokenApi(pdo, role)
            .then((data) => {
                Logger.log('Authentification Fake: Terminee - Token obtenu.');
                setJWT(data);
                setToken(data);
            })
            // error with simulator
            .catch(() => {
                deleteJWT();
                setToken('fakeToken');
            });
    };

    const retrieveRealToken = async () => {
        // On recupere un token de test
        Logger.log(
            "Authentification Auth0: Terminee - obtention d'un token..."
        );
        setToken(
            await authentification
                .getAccessTokenContext()
                .then((accessTokenContext) => {
                    Logger.log(
                        'Authentification Auth0: Terminee - Token obtenu.'
                    );
                    return accessTokenContext.signatureJWT;
                })
                .catch(() => {
                    return '';
                })
        );
    };

    useEffect(() => {
        if (shouldUseAuth0 === true) {
            // Si besoin, nous allons initialiser auth0
            Logger.log('Authentification Auth0: Demarrage...');
            window.addEventListener(
                '@authentification/v1/chargement-session-complete',
                retrieveRealToken
            );
            authentification.initialiser();
        } else if (shouldUseAuth0 === false) {
            // Sinon on utilise un fake token du simulateur
            if (mockEnabled === false && cookieConfigAttributes?.pdo) {
                Logger.log('Authentification Fake: Demarrage...');
                retrieveFakeToken(
                    cookieConfigAttributes?.pdo,
                    cookieConfigAttributes?.role
                );
            } else {
                //Sinon nous sommes en mode "mock integree" donc pas besoin de token
                deleteJWT();
                setToken('fakeToken');
            }
        }
    }, [shouldUseAuth0]);

    useEffect(() => {
        Logger.log('Authentification: DEBUT - Identification.');
        if (envConfig.type === EnvType.PROD) {
            // PROD: enable
            return setShouldUseAuth0(true);
        }

        // NonProd Logic - AccesDCGateway + Mock disabled == Auth0
        if (envConfig.type === EnvType.NONPROD_GATEWAY && !mockEnabled) {
            return setShouldUseAuth0(true);
        } else {
            return setShouldUseAuth0(false);
        }
    }, []);

    useEffect(() => {
        //Fin de l'authentification si on sait quel methode utilise et si on a obtenu un token (vrai ou de test)
        if (shouldUseAuth0 !== null && token) {
            Logger.log("Authentification: FIN - Lancement de l'application.");
            setIsAuthenticated(true);
        }
    }, [token, shouldUseAuth0]);

    //Attendre le retour de l'appel pour obtenir le statut des toggles
    useEffect(() => {
        if (toggle) {
            if (toggle.clavardageActif === true && token !== null) {
                //Injecter le token au clavardage
                setJetonClavardage(token);
                // On active le clavardage si on est dans le parcours "normal" et pas sur l'app mobile
                if (deepLinkMode === false && isMobileApp === false) {
                    Logger.log('Activation du clavardage');
                    // Injecter le clavardage ainsi que le token
                    injecterClavardage(envConfig.isProd);
                }
            }
            // Activer la suite du chargement de l'application
            setIsReadyForAPI(true);
        }
    }, [toggle]);

    return (
        <>
            <div className={classes.autoContainer}>
                <LoadingScreen show={globalLoading} />
                {/* Classic views */}
                {isReadyForAPI && (
                    <View
                        envConfig={envConfig}
                        langue={langue}
                        isAuth0Enabled={shouldUseAuth0}
                        isAuthenticated={isAuthenticated}
                        isNonProdEnv={envConfig.type !== EnvType.PROD}
                        isDeepLink={deepLinkMode}
                        isMobileApp={isMobileApp}
                    />
                )}
            </div>

            {isReadyForAPI && !deepLinkMode && !globalLoading && (
                <>
                    <RenderSupportBar />
                    <RenderNoteLegal />
                </>
            )}
        </>
    );
};

// Static Content: Header and footer
const App = () => {
    const [isGridVisible, setIsGridVisible] = useState(false);
    const theme = useTheme();

    // const
    const query = URLToURLSearchParams();
    const dlParam = query.get(QUERY_PARAM_DEEP_LINK);
    const mockEnabled = isMockEnabled();
    let deepLinkMode = false;

    // Setup langue
    const langue = getLangue();
    i18n.changeLanguage(langue);
    moment.locale(langue);
    document.documentElement.lang = langue;

    // deepLink (Before env config)
    if ('true' === dlParam || (getPretIdDemande() === null && !mockEnabled)) {
        // Mode DeepLink: Ouvrir la vue pour obtenir les demandes
        deepLinkMode = true;
        // Si deepLink -> On enleve le cookie config pour ne pas avoir de conflit
        deleteCookie(JWT_COOKIE_CONFIG_NAME, DOMAIN_DESJARDINS_COOKIE, '/');
    } else {
        //Si on est pas en DeepLink: On met la langue detectee dans le cookie config pour eviter tout comportement non voulu dans RHN
        setLangueInCookies(langue);
    }

    // Cleanup TNR cookie
    deleteCookie(TNR_COOKIE, DOMAIN_DESJARDINS_COOKIE, '/');

    // Init mock if required
    if (shouldUseMock()) {
        useMock();
    }

    // Localhost only -> grid
    if (process.env.NODE_ENV === 'development') {
        useHotkeys('g', () => setIsGridVisible((prevState) => !prevState));
    }

    useEffect(() => {
        // Setup unique RHN Session Id
        document
            .querySelector('meta[name="rhn-session-id"]')
            .setAttribute('content', uuidv4());
        // Dynatrace
        addDynatraceTagScript(isEnvNonProd());
        // On desactive le bouton retour sur l'application. Il faut utiliser la navigation
        window.history.pushState(null, '', window.location.href);
        window.onpopstate = function () {
            window.history.pushState(null, '', window.location.href);
        };
    }, []);

    return (
        <>
            {isGridVisible && <GridHelper></GridHelper>}
            <Box
                display="flex"
                flexDirection="column"
                minHeight="100vh"
                bgcolor={theme.palette.grey[200]}
            >
                <RenderHeader />
                {/* VIEW CONTENT */}
                <Auth0
                    langue={langue}
                    deepLinkMode={deepLinkMode}
                    mockEnabled={mockEnabled}
                />
                <RenderFooter />
            </Box>
        </>
    );
};

// Init Flow : Root -> App -> Auth0 -> View -> RenderRoutes -> RenderComponent
export const Root: React.FC<unknown> = () => (
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} />
            <ThemeProvider theme={theme}>
                <Router basename={'/'}>
                    <ScrollToTop />
                    <Provider store={store}>
                        <ErrorBoundary FallbackComponent={RenderErreur}>
                            <App />
                        </ErrorBoundary>
                    </Provider>
                </Router>
            </ThemeProvider>
        </QueryClientProvider>
    </React.StrictMode>
);
